import {
  Autocomplete,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import GlobalHeader from "../../../components/GlobalHeader";
import { useEffect, useMemo, useState } from "react";
import useApi from "../../../hooks/useApi";
import { LoadingButton } from "@mui/lab";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";
import TableStyled from "../../../components/table";

const initValues = {
  code_voucher: "",
  description: "",
  radar_coin: "",
  start_discount: "",
  end_discount: "",
  max_canjes: "",
};

// max_canjes es opcional (vacío = sin límite): se excluye de la validación
// de "todos los campos son requeridos".
const camposRequeridos = Object.keys(initValues).filter(
  (key) => key !== "max_canjes"
);

// Valida un formulario de bono (crear o editar). Devuelve el mensaje de
// error o null si está todo bien. Refleja las mismas reglas del backend.
const validarFormularioBono = (form) => {
  if (camposRequeridos.some((key) => !form[key]))
    return "Todos los campos son requeridos.";
  // El día de fin cuenta completo: inicio y fin iguales es un bono de un día.
  if (form.start_discount > form.end_discount)
    return "La fecha final no puede ser anterior a la de inicio.";
  const rc = Number(form.radar_coin);
  if (Number.isNaN(rc) || rc <= 0)
    return "El valor en RadarCoins debe ser mayor a 0.";
  return null;
};

// Vigencia por días de calendario en la hora del negocio, igual que el
// backend (Backend/src/utils/fechasBono.js): el bono vale desde las 00:00
// del día de inicio hasta las 23:59 del día de fin. Las fechas llegan como
// medianoche UTC del día elegido, por eso se toma su parte UTC.
const ZONA_HORARIA = "America/Mexico_City";
const diaDelBono = (valor) => {
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};
const hoyEnZona = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA_HORARIA,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

// Estado visual del bono: agotado > vencido > inactivo > programado > activo.
const estadoDeBono = (bono) => {
  const hoy = hoyEnZona();
  if (bono.max_canjes && bono.canjes >= bono.max_canjes)
    return { label: "Agotado", color: "error" };
  if (hoy > diaDelBono(bono.end_discount))
    return { label: "Vencido", color: "warning" };
  if (!bono.status) return { label: "Inactivo", color: "default" };
  // Todavía no empezó: no se puede canjear hasta su día de inicio.
  if (hoy < diaDelBono(bono.start_discount))
    return { label: "Programado", color: "info" };
  return { label: "Activo", color: "success" };
};

const Bonos = () => {
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [form, setForm] = useState(initValues);
  const [artistasSeleccionados, setArtistasSeleccionados] = useState([]);
  const [data, setData] = useState([]);
  const [artistas, setArtistas] = useState([]);
  const [canjesAbierto, setCanjesAbierto] = useState(null); // bono seleccionado
  const [canjesUsuarios, setCanjesUsuarios] = useState([]);
  const [editando, setEditando] = useState(null); // bono en edición, o null
  const [editForm, setEditForm] = useState(initValues);
  const [editArtistasSeleccionados, setEditArtistasSeleccionados] = useState(
    []
  );

  useEffect(() => {
    getVouchers();
    loadApi("admin/users", true, "get")
      .then((res) =>
        setArtistas((res.data || []).filter((u) => u.role === "artista"))
      )
      .catch(() => {});
  }, []);

  const getVouchers = async () => {
    try {
      const { data } = await loadApi("admin/bonos", true, "get");
      setData(data);
    } catch (error) {
      showSnack(error.message, "error");
    }
  };

  const artistaPorId = useMemo(
    () => new Map(artistas.map((a) => [a.id, a])),
    [artistas]
  );

  const destinatariosLabelDe = (bono) => {
    const ids = Array.isArray(bono.artistas) ? bono.artistas : [];
    return ids.length === 0
      ? "General"
      : ids
          .map((id) => artistaPorId.get(id)?.name)
          .filter(Boolean)
          .join(", ") || `${ids.length} artista(s)`;
  };

  // Enriquece cada bono con las etiquetas de destinatarios y de canjes para
  // la tabla ("General" o los nombres de los artistas; "3 / 15" o "3" si no
  // tiene límite, marcando "agotado" cuando llegó al cupo).
  const dataConDestinatarios = useMemo(
    () =>
      data.map((bono) => {
        const canjesLabel = bono.max_canjes
          ? `${bono.canjes} / ${bono.max_canjes}${
              bono.canjes >= bono.max_canjes ? " (agotado)" : ""
            }`
          : `${bono.canjes}`;
        return {
          ...bono,
          destinatariosLabel: destinatariosLabelDe(bono),
          canjesLabel,
        };
      }),
    [data, artistaPorId]
  );

  const handleChange = ({ target }) => {
    const { value, name } = target;
    setForm((props) => ({
      ...props,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorValidacion = validarFormularioBono(form);
    if (errorValidacion) {
      showSnack(errorValidacion, "warning");
      return;
    }

    const body = {
      ...form,
      code_voucher: form.code_voucher.trim().toUpperCase(),
      radar_coin: Number(form.radar_coin),
      // Vacío = bono general, disponible para cualquier artista.
      artistas: artistasSeleccionados.map((a) => a.id),
      // Vacío = sin límite de canjes.
      max_canjes: form.max_canjes ? Number(form.max_canjes) : null,
    };

    try {
      const { data } = await loadApi("admin/bonos/create", true, "post", body);
      showSnack(data.message, "success");
      setForm(initValues);
      setArtistasSeleccionados([]);
      getVouchers();
    } catch (error) {
      showSnack(String(error.message), "error");
    }
  };

  const handleVerCanjes = async (bono) => {
    setCanjesAbierto(bono);
    setCanjesUsuarios([]);
    try {
      const { data } = await loadApi(
        `admin/bonos/${bono.id}/canjes`,
        true,
        "get"
      );
      setCanjesUsuarios(data || []);
    } catch (error) {
      showSnack(error.message, "error");
    }
  };

  const handleAbrirEditar = (bono) => {
    setEditando(bono);
    setEditForm({
      code_voucher: bono.code_voucher,
      description: bono.description,
      radar_coin: String(bono.radar_coin),
      start_discount: String(bono.start_discount).slice(0, 10),
      end_discount: String(bono.end_discount).slice(0, 10),
      max_canjes: bono.max_canjes ? String(bono.max_canjes) : "",
    });
    const ids = Array.isArray(bono.artistas) ? bono.artistas : [];
    setEditArtistasSeleccionados(
      ids.map((id) => artistaPorId.get(id)).filter(Boolean)
    );
  };

  const handleEditChange = ({ target }) => {
    const { value, name } = target;
    setEditForm((props) => ({ ...props, [name]: value }));
  };

  const handleGuardarEdicion = async () => {
    const errorValidacion = validarFormularioBono(editForm);
    if (errorValidacion) {
      showSnack(errorValidacion, "warning");
      return;
    }

    try {
      const { data } = await loadApi(
        `admin/bonos/${editando.id}`,
        true,
        "put",
        {
          description: editForm.description,
          radar_coin: Number(editForm.radar_coin),
          start_discount: editForm.start_discount,
          end_discount: editForm.end_discount,
          artistas: editArtistasSeleccionados.map((a) => a.id),
          max_canjes: editForm.max_canjes ? Number(editForm.max_canjes) : null,
        }
      );
      showSnack(data.message, "success");
      setEditando(null);
      getVouchers();
    } catch (error) {
      showSnack(String(error.message), "error");
    }
  };

  const handleToggleActivo = async (bono) => {
    try {
      const { data } = await loadApi(`admin/bonos/${bono.id}`, true, "put", {
        status: !bono.status,
      });
      showSnack(data.message, "success");
      getVouchers();
    } catch (error) {
      showSnack(String(error.message), "error");
    }
  };

  const headersTable = useMemo(
    () => [
      { label: "CODIGO", ref: "code_voucher" },
      { label: "DESCRIPCION", ref: "description" },
      { label: "FECHA INICIO", ref: "start_discount" },
      { label: "FECHA FIN", ref: "end_discount" },
      { label: "CONTENIDO", ref: "radar_coin" },
      { label: "DESTINATARIOS", ref: "destinatariosLabel" },
      // sortRef: ordena por el número real de canjes, no por el texto "3 / 15"
      { label: "CANJES", ref: "canjesLabel", sortRef: "canjes" },
      {
        label: "ESTADO",
        ref: "estadoLabel",
        render: (bono) => {
          const { label, color } = estadoDeBono(bono);
          return <Chip label={label} color={color} size="small" />;
        },
      },
      {
        label: "ACCIONES",
        ref: "acciones",
        render: (bono) => (
          <Stack direction="row" justifyContent="center">
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAbrirEditar(bono);
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={bono.status ? "Desactivar" : "Activar"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleActivo(bono);
                }}
              >
                {bono.status ? (
                  <ToggleOnIcon fontSize="small" color="success" />
                ) : (
                  <ToggleOffOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [artistaPorId]
  );

  return (
    <>
      <GlobalHeader />
      <Container>
        <Stack spacing={4}>
          {/* Agregar nuevo */}
          <Stack bgcolor="#f1f1f1" p={3} borderRadius="10px" spacing={4}>
            <Typography variant="subtitle1">Agregar bono de regalo</Typography>
            <Stack
              component="form"
              role="form"
              onSubmit={handleSubmit}
              spacing={2}
            >
              <TextField
                label="Codigo"
                name="code_voucher"
                value={form.code_voucher}
                onChange={handleChange}
              />
              <TextField
                multiline
                rows={4}
                label="Descripcion"
                name="description"
                value={form.description}
                onChange={handleChange}
              />
              <Stack
                spacing={2}
                direction={{ xs: "column", md: "row" }}
                alignItems={{ md: "flex-end" }}
              >
                <Stack flex={0.5}>
                  <TextField
                    label="Radar coin"
                    name="radar_coin"
                    value={form.radar_coin}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    sx={{
                      "input::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />
                </Stack>
                <Stack flex={0.5}>
                  <TextField
                    label="Límite de canjes (opcional)"
                    name="max_canjes"
                    value={form.max_canjes}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    placeholder="Ej: 15 (vacío = sin límite)"
                    sx={{
                      "input::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />
                </Stack>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  flex={1}
                >
                  <Stack flex={1}>
                    <FormLabel>Fecha inicial del bono</FormLabel>
                    <TextField
                      name="start_discount"
                      value={form.start_discount}
                      onChange={handleChange}
                      type="date"
                    />
                  </Stack>
                  <Stack flex={1}>
                    <FormLabel>Fecha final del bono</FormLabel>
                    <TextField
                      name="end_discount"
                      value={form.end_discount}
                      onChange={handleChange}
                      type="date"
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Stack spacing={1}>
                <FormLabel>
                  Destinatarios (uno, varios artistas, o déjalo vacío para un
                  código general)
                </FormLabel>
                <Autocomplete
                  multiple
                  options={artistas}
                  value={artistasSeleccionados}
                  onChange={(e, value) => setArtistasSeleccionados(value)}
                  getOptionLabel={(a) => `${a.name || "Sin nombre"} — ${a.username}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar artista por nombre o email..."
                    />
                  )}
                />
              </Stack>
              <Stack alignItems="flex-end">
                <LoadingButton
                  loading={loadingApi.includes("post__admin/bonos/create")}
                  sx={{ padding: "7px 32px" }}
                  type="submit"
                >
                  Crear
                </LoadingButton>
              </Stack>
            </Stack>
          </Stack>
          {/* Tabla para ver los bonos */}
          <Typography variant="caption" color="grayText">
            Haz clic en un bono para ver qué usuarios lo canjearon. Usa los
            íconos para editarlo o desactivarlo.
          </Typography>
          <TableStyled
            headers={headersTable}
            data={dataConDestinatarios}
            onRowClick={handleVerCanjes}
            loading={loadingApi.includes("get___admin/bonos")}
          />
        </Stack>
      </Container>

      {/* Trazabilidad: quién canjeó el bono seleccionado */}
      <Dialog
        open={Boolean(canjesAbierto)}
        onClose={() => setCanjesAbierto(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Canjes de {canjesAbierto?.code_voucher}
          {canjesAbierto?.max_canjes && (
            <Typography variant="body2" color="grayText">
              {canjesAbierto.canjes} de {canjesAbierto.max_canjes} canjes usados
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {canjesUsuarios.length === 0 ? (
            <Typography variant="body2" color="grayText">
              {loadingApi.includes(
                `get__admin/bonos/${canjesAbierto?.id}/canjes`
              )
                ? "Cargando..."
                : "Nadie ha canjeado este bono todavía."}
            </Typography>
          ) : (
            <List>
              {canjesUsuarios.map((u) => (
                <ListItem key={u.id} divider>
                  <ListItemText
                    primary={u.nombre}
                    secondary={`${u.email}${
                      u.nombreMedia ? ` — ${u.nombreMedia}` : ""
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Editar bono */}
      <Dialog
        open={Boolean(editando)}
        onClose={() => setEditando(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar bono {editando?.code_voucher}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField
              label="Código"
              value={editForm.code_voucher}
              disabled
              helperText="El código no se puede cambiar una vez creado."
            />
            <TextField
              multiline
              rows={4}
              label="Descripcion"
              name="description"
              value={editForm.description}
              onChange={handleEditChange}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Radar coin"
                name="radar_coin"
                value={editForm.radar_coin}
                onChange={handleEditChange}
                type="number"
                fullWidth
              />
              <TextField
                label="Límite de canjes (opcional)"
                name="max_canjes"
                value={editForm.max_canjes}
                onChange={handleEditChange}
                type="number"
                placeholder="Ej: 15 (vacío = sin límite)"
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Stack flex={1}>
                <FormLabel>Fecha inicial</FormLabel>
                <TextField
                  name="start_discount"
                  value={editForm.start_discount}
                  onChange={handleEditChange}
                  type="date"
                />
              </Stack>
              <Stack flex={1}>
                <FormLabel>Fecha final</FormLabel>
                <TextField
                  name="end_discount"
                  value={editForm.end_discount}
                  onChange={handleEditChange}
                  type="date"
                />
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <FormLabel>Destinatarios</FormLabel>
              <Autocomplete
                multiple
                options={artistas}
                value={editArtistasSeleccionados}
                onChange={(e, value) => setEditArtistasSeleccionados(value)}
                getOptionLabel={(a) => `${a.name || "Sin nombre"} — ${a.username}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar artista por nombre o email..."
                  />
                )}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setEditando(null)}>
            Cancelar
          </LoadingButton>
          <LoadingButton
            loading={loadingApi.includes(`put__admin/bonos/${editando?.id}`)}
            onClick={handleGuardarEdicion}
          >
            Guardar cambios
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
};

export default Bonos;
